/**
 * Settings → Patterns → Launch Types and Investment Types.
 *
 * Built to the same standard as Work Types: built-in and custom types in their
 * own sections, each card saying what the type carries, a count of the types
 * that carry nothing, and the type's pattern (Certainty, Shape, Workload, Bar —
 * docs/9-exploration/TYPE-PATTERNS-PRD.md §3.1) edited and versioned in place
 * through the shared PatternEditor. The catalog itself (name, description, on
 * or off) stays in workspace_launch_types / workspace_investment_types.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, Loader2, Lock, Pencil, PieChart, Plus, Rocket, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { PatternEditor } from '../../../components/prioritization/typePatterns/PatternEditor';
import { Field, TextArea, TextInput } from '../../../components/ds';
import { btnPrimary, btnSecondary } from '../../../components/mono/primitives';
import { useItemTaxonomyStore } from '../../../stores/prioritization/itemTaxonomyStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useTypePatternsStore } from '../../../stores/prioritization/typePatternsStore';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import { useTypeListPickers, type TypeListWithOptions } from '../../../hooks/prioritization/useTypeListPickers';
import { getLaunchProfiles } from '../../../lib/timePhasedPlanningService';
import type { LaunchProfile } from '../../../lib/timePhasedPlanning.types';
import {
  ANCHOR_LABEL,
  DATE_FIRMNESS_LABEL,
  HOURS_PER_CU,
  QUALITY_LABEL,
  isEmptyPattern,
  patternSummary,
} from '../../../lib/typePatterns';
import { fetchPatternHistory, mayEditTypeList, patternPolicyBlock } from '../../../lib/typePatternsService';
import type { ItemTaxonomyKind, WorkspaceItemTaxonomy } from '../../../lib/itemTaxonomyService';
import type { TypePattern } from '../../../types/prioritization/typePatterns';

// ── What a pattern says, part by part ────────────────────────────────────────

function PatternDetail({ pattern, teamLabel }: { pattern: TypePattern; teamLabel: (k: string) => string }) {
  const { certainty, shape, workload, bar } = pattern;
  const rows: Array<[string, string]> = [];

  const barBits = [
    bar.date ? DATE_FIRMNESS_LABEL[bar.date] : null,
    bar.quality ? QUALITY_LABEL[bar.quality] : null,
    bar.firmness !== undefined ? `slides last ${bar.firmness}/10` : null,
    bar.signOff?.length ? `signed off by ${bar.signOff.join(', ')}` : null,
  ].filter(Boolean);
  if (barBits.length) rows.push(['Bar', barBits.join(' · ')]);

  const certBits = [
    certainty.scheduleSlackWeeks !== undefined
      ? `${certainty.scheduleSlackWeeks} week${certainty.scheduleSlackWeeks === 1 ? '' : 's'} of slack`
      : null,
    certainty.confidenceCeiling !== undefined ? `at most ${Math.round(certainty.confidenceCeiling * 100)}% confident` : null,
    certainty.beforeScheduling?.length ? `before scheduling: ${certainty.beforeScheduling.join('; ')}` : null,
  ].filter(Boolean);
  if (certBits.length) rows.push(['Certainty', certBits.join(' · ')]);

  if (shape.stages?.length) {
    rows.push([
      'Shape',
      `${shape.stages.map((s) => `${s.label} (${s.weeks} wk)`).join(' → ')}${shape.anchor ? `, from ${ANCHOR_LABEL[shape.anchor].toLowerCase()}` : ''}`,
    ]);
  }

  if (workload.lines?.length) {
    const wk = (n: number) => (n < 0 ? `${n}` : `+${n}`);
    rows.push([
      'Workload',
      workload.lines
        .map((l) => `${teamLabel(l.resourceType)} ${l.capacityUnits} CU (≈${Math.round(l.capacityUnits * HOURS_PER_CU)} h), weeks ${wk(l.fromWeek)} to ${wk(l.toWeek)}`)
        .join(' · '),
    ]);
  }

  return (
    <dl className="mt-2 grid gap-1 text-xs" data-testid="pattern-detail">
      {rows.map(([part, text]) => (
        <div key={part} className="flex gap-2">
          <dt className="w-20 flex-shrink-0 font-medium text-[var(--text-ter)]">{part}</dt>
          <dd className="m-0 text-[var(--text-sec)]">{text}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── One type ─────────────────────────────────────────────────────────────────

interface TypeCardProps {
  item: WorkspaceItemTaxonomy;
  kindNoun: string;
  list: TypeListWithOptions | null;
  pattern: TypePattern | null;
  canEditCatalog: boolean;
  mayEditPattern: boolean | undefined;
  teamLabel: (k: string) => string;
  resourceTypes: Array<{ key: string; label: string }>;
  workloadTemplates: LaunchProfile[];
  onToggle: (active: boolean) => void;
  onSaveDetails: (label: string, description: string | null) => Promise<void>;
  onDelete: () => void;
  onPatternSaved: (p: TypePattern) => void;
}

function TypeCard({
  item,
  kindNoun,
  list,
  pattern,
  canEditCatalog,
  mayEditPattern,
  teamLabel,
  resourceTypes,
  workloadTemplates,
  onToggle,
  onSaveDetails,
  onDelete,
  onPatternSaved,
}: TypeCardProps) {
  const [editingDetails, setEditingDetails] = useState(false);
  const [editingPattern, setEditingPattern] = useState(false);
  const [label, setLabel] = useState(item.label);
  const [description, setDescription] = useState(item.description ?? '');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<TypePattern[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const hasPattern = !!pattern && !isEmptyPattern(pattern);

  async function saveDetails() {
    if (!label.trim()) return;
    setSaving(true);
    await onSaveDetails(label.trim(), description.trim() || null);
    setSaving(false);
    setEditingDetails(false);
  }

  async function toggleHistory() {
    if (historyOpen) return setHistoryOpen(false);
    setHistoryOpen(true);
    if (history === null && list) {
      setHistory(await fetchPatternHistory(list.workspaceId, list.key, item.value));
    }
  }

  return (
    <div
      className={`border p-4 ${item.isActive ? 'bg-[var(--bg-raised)] border-[var(--rule)]' : 'bg-[var(--surface)] border-[var(--rule)] opacity-70'}`}
      data-testid={`type-card-${item.value}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="m-0 text-sm font-semibold text-[var(--text-pri)]">{item.label}</h3>
            <code className="rounded bg-[var(--hover-tint)] px-1.5 py-0.5 text-[11px] text-[var(--text-sec)]">{item.value}</code>
            {item.isBuiltin ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--text-ter)]">
                <Lock className="h-3 w-3" /> Built-in
              </span>
            ) : (
              <span className="text-[11px] font-medium text-[var(--text-ter)]">Custom</span>
            )}
            {!item.isActive && <span className="text-[11px] font-medium text-[var(--mustard)]">Switched off</span>}
            {pattern && (
              <button
                type="button"
                onClick={() => void toggleHistory()}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                title="Earlier versions of this pattern"
              >
                <History className="h-3 w-3" /> v{pattern.versionNumber}
              </button>
            )}
          </div>

          {hasPattern ? (
            <>
              <p className="m-0 mt-1 text-xs text-[var(--text-ter)]">{patternSummary(pattern)}</p>
              <PatternDetail pattern={pattern} teamLabel={teamLabel} />
            </>
          ) : (
            <p className="m-0 mt-1 text-xs text-[var(--mustard)]">
              <span className="font-medium">No pattern.</span> Planning assumes nothing from this {kindNoun}: no date
              firmness, quality bar, stages or team load.
            </p>
          )}

          {item.description && <p className="m-0 mt-2 text-sm text-[var(--text-sec)]">{item.description}</p>}
        </div>

        {!editingDetails && !editingPattern && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {canEditCatalog && (
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-[var(--text-sec)]">
                <input
                  type="checkbox"
                  className="rounded border-[var(--rule)]"
                  checked={item.isActive}
                  onChange={(e) => onToggle(e.target.checked)}
                />
                Active
              </label>
            )}
            {mayEditPattern && list && (
              <button type="button" onClick={() => setEditingPattern(true)} {...btnSecondary('text-xs')}>
                {hasPattern ? 'Edit pattern' : 'Add pattern'}
              </button>
            )}
            {canEditCatalog && (
              <button
                type="button"
                onClick={() => {
                  setLabel(item.label);
                  setDescription(item.description ?? '');
                  setEditingDetails(true);
                }}
                title="Edit name and description"
                aria-label={`Edit ${item.label}`}
                className="p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)]"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {canEditCatalog && !item.isBuiltin && (
              <button
                type="button"
                onClick={onDelete}
                title={`Delete this custom ${kindNoun}`}
                aria-label={`Delete ${item.label}`}
                className="p-1.5 text-[var(--coral)] hover:bg-[var(--coral-dim)]"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {historyOpen && (
        <div className="mt-3 border-t border-[var(--rule)] pt-3 text-xs" data-testid="pattern-history">
          {history === null ? (
            <p className="m-0 text-[var(--text-ter)]">Loading earlier versions…</p>
          ) : history.length === 0 ? (
            <p className="m-0 text-[var(--text-ter)]">No earlier versions.</p>
          ) : (
            <ul className="m-0 list-none space-y-1 p-0">
              {history.map((v) => (
                <li key={v.id} className="text-[var(--text-sec)]">
                  <span className="font-medium text-[var(--text-pri)]">v{v.versionNumber}</span>
                  {' · '}
                  {new Date(v.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' · '}
                  {patternSummary(v)}
                  {v.reason && <span className="text-[var(--text-ter)]"> — {v.reason}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {editingDetails && (
        <div className="mt-3 grid gap-3 border border-[var(--rule)] bg-[var(--surface)] p-4">
          <Field label="Name">
            <TextInput value={label} onChange={(e) => setLabel(e.target.value)} />
          </Field>
          <Field label="Description">
            <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditingDetails(false)} {...btnSecondary()}>Cancel</button>
            <button type="button" disabled={saving || !label.trim()} onClick={() => void saveDetails()} {...btnPrimary('disabled:opacity-50')}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {editingPattern && list && (
        <PatternEditor
          list={list}
          type={{ id: null, value: item.value, label: item.label, description: item.description, isActive: item.isActive }}
          current={pattern}
          workTypeFacts={null}
          resourceTypes={resourceTypes}
          workloadTemplates={workloadTemplates}
          onCancel={() => setEditingPattern(false)}
          onSaved={(saved) => {
            onPatternSaved(saved);
            setHistory(null);
            setEditingPattern(false);
          }}
        />
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ItemTaxonomySettingsPage({
  kind,
  title,
  subtitle,
  icon,
  kindNoun,
  intro,
}: {
  kind: ItemTaxonomyKind;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  /** "launch type" / "investment type" — used in sentences. */
  kindNoun: string;
  intro: React.ReactNode;
}) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const can = useUserStore((s) => s.can);
  const canEditCatalog = can('admin.settings');

  const state = useItemTaxonomyStore((s) => s[kind]);
  const fetch = useItemTaxonomyStore((s) => s.fetch);
  const toggleActive = useItemTaxonomyStore((s) => s.toggleActive);
  const rename = useItemTaxonomyStore((s) => s.rename);
  const add = useItemTaxonomyStore((s) => s.add);
  const remove = useItemTaxonomyStore((s) => s.remove);

  const { lists } = useTypeListPickers(workspaceId);
  const list = lists.find((l) => l.key === kind) ?? null;
  const patternFor = useTypePatternsStore((s) => s.patternFor);
  useTypePatternsStore((s) => s.patterns);
  const putPattern = useTypePatternsStore((s) => s.putPattern);

  const loadResourceTypes = useResourceTypesStore((s) => s.load);
  const { getEnabledTypes, getAllTypes } = useResourceTypesStore();
  const resourceTypes = getEnabledTypes().map((t) => ({ key: t.key, label: t.label }));
  const teamLabel = (k: string) => getAllTypes().find((t) => t.key === k)?.label ?? k;
  const [workloadTemplates, setWorkloadTemplates] = useState<LaunchProfile[]>([]);
  const [mayEditPattern, setMayEditPattern] = useState<boolean | undefined>(undefined);
  const [patternBlock, setPatternBlock] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    void fetch(kind, workspaceId);
    void loadResourceTypes(workspaceId);
    getLaunchProfiles(workspaceId).then(setWorkloadTemplates).catch(() => setWorkloadTemplates([]));
  }, [workspaceId, kind, fetch, loadResourceTypes]);

  // Who may edit this list's patterns: asked of the database, the rule it enforces.
  const listId = list?.id ?? null;
  useEffect(() => {
    if (!listId || !userId) return;
    let live = true;
    void mayEditTypeList(listId, userId).then((ok) => {
      if (live) setMayEditPattern(ok);
    });
    // And whether a policy holds back pattern changes — the check revise_type_pattern makes.
    if (workspaceId) {
      void patternPolicyBlock(workspaceId, listId).then((reason) => {
        if (live) setPatternBlock(reason);
      });
    }
    return () => {
      live = false;
    };
  }, [listId, userId, workspaceId]);

  const builtins = useMemo(() => state.items.filter((i) => i.isBuiltin), [state.items]);
  const customs = useMemo(() => state.items.filter((i) => !i.isBuiltin), [state.items]);
  const active = state.items.filter((i) => i.isActive);
  const withoutPattern = active.filter((i) => {
    const p = patternFor(kind, i.value);
    return !p || isEmptyPattern(p);
  });

  async function handleAdd() {
    if (!workspaceId || !newLabel.trim()) return;
    setSaving(true);
    await add(kind, workspaceId, newLabel.trim(), newDescription.trim() || null);
    setSaving(false);
    setNewLabel('');
    setNewDescription('');
    setAdding(false);
  }

  const card = (item: WorkspaceItemTaxonomy) => (
    <TypeCard
      key={item.id}
      item={item}
      kindNoun={kindNoun}
      list={list}
      pattern={patternFor(kind, item.value)}
      canEditCatalog={canEditCatalog}
      mayEditPattern={mayEditPattern && !patternBlock}
      teamLabel={teamLabel}
      resourceTypes={resourceTypes}
      workloadTemplates={workloadTemplates}
      onToggle={(on) => void toggleActive(kind, item.id, on)}
      onSaveDetails={(label, description) => rename(kind, item.id, label, description)}
      onDelete={() => {
        if (confirm(`Delete this custom ${kindNoun}? Items keep the value until they are given another.`)) void remove(kind, item.id);
      }}
      onPatternSaved={putPattern}
    />
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader icon={icon} iconBg="bg-[var(--slate-dim)]" title={title} subtitle={subtitle} />
        {canEditCatalog && (
          <button type="button" onClick={() => setAdding(true)} className="btn btn-primary mt-1 flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Custom Type
          </button>
        )}
      </div>

      <div className="border border-[var(--slate)] bg-[var(--slate-dim)]/50 px-4 py-3 text-sm text-[var(--text-ter)]">
        {intro}
      </div>

      {/* An empty pattern is silence everywhere else in the product, so the gap
          is counted on the page that owns it. */}
      {!state.loading && active.length > 0 && withoutPattern.length > 0 && (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)]/50 px-4 py-3 text-sm text-[var(--mustard)]" data-testid="missing-patterns">
          <span className="font-medium">
            {withoutPattern.length} of {active.length} {kindNoun}
            {active.length === 1 ? '' : 's'} {withoutPattern.length === 1 ? 'has' : 'have'} no pattern
          </span>{' '}
          ({withoutPattern.map((i) => i.label).join(', ')}). Until one is set, choosing that {kindNoun} on an item tells
          planning nothing about how firm its date is, the bar it is held to, how it unfolds, or which teams it needs.
        </div>
      )}

      {state.error && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3 text-sm text-[var(--coral)]">{state.error}</div>
      )}

      {adding && (
        <div className="grid gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
          <h2 className="m-0 text-sm font-semibold text-[var(--text-pri)]">New custom {kindNoun}</h2>
          <Field label="Name">
            <TextInput
              autoFocus
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setAdding(false);
              }}
            />
          </Field>
          <Field label="Description" hint={`What makes something this ${kindNoun}.`}>
            <TextArea rows={2} value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
          </Field>
          <p className="m-0 text-xs text-[var(--text-ter)]">Add its pattern from its card once it is saved.</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(false)} {...btnSecondary()}>Cancel</button>
            <button type="button" disabled={saving || !newLabel.trim()} onClick={() => void handleAdd()} {...btnPrimary('disabled:opacity-50')}>
              {saving ? 'Saving…' : `Add ${kindNoun}`}
            </button>
          </div>
        </div>
      )}

      {state.loading && state.items.length === 0 ? (
        <div className="flex items-center gap-2 py-8 text-sm text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading {kindNoun}s…
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-sec)]">Built-in Types</h2>
            <div className="grid gap-3">{builtins.map(card)}</div>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-sec)]">Custom Types</h2>
            {customs.length === 0 ? (
              <p className="text-sm text-[var(--text-ter)]">
                None yet.{canEditCatalog ? ` Add one for a kind of ${kindNoun.replace(' type', '')} the built-ins don't cover.` : ''}
              </p>
            ) : (
              <div className="grid gap-3">{customs.map(card)}</div>
            )}
          </section>
        </>
      )}

      {mayEditPattern && patternBlock && (
        <p className="text-xs text-[var(--text-ter)]">Changing these patterns: {patternBlock}</p>
      )}
      {mayEditPattern === false && (
        <p className="text-xs text-[var(--text-ter)]">
          Changing these patterns needs an admin or one of the list&apos;s owners (
          <Link to="/prioritization/settings/type-patterns" className="underline">Type Lists &amp; Patterns</Link>).
        </p>
      )}
    </div>
  );
}

export default function LaunchTypesSettings() {
  return (
    <ItemTaxonomySettingsPage
      kind="launch_type"
      kindNoun="launch type"
      title="Launch Types"
      subtitle="How something goes to market — product, feature, packaging, partner. Built-in types are shared; add custom types for this workspace."
      icon={<Rocket className="h-4 w-4 text-[var(--text-ter)]" />}
      intro={
        <>
          A launch type tells Planning what usually comes with this kind of launch: how firm its date is and the bar it is
          held to, the stages it goes through, and which teams it needs in the weeks around the date. A product launch at a
          conference carries a fixed date and a polished bar; a soft launch can slip and ship rough. Each type&apos;s
          pattern is versioned — a plan keeps the version it read.
        </>
      }
    />
  );
}

export function InvestmentTypesSettings() {
  return (
    <ItemTaxonomySettingsPage
      kind="investment_type"
      kindNoun="investment type"
      title="Investment Types"
      subtitle="What the spend is meant to do — grow, maintain, cut cost, reduce risk, or place a bet. Built-in types are shared; add custom types for this workspace."
      icon={<PieChart className="h-4 w-4 text-[var(--text-ter)]" />}
      intro={
        <>
          An investment type tells Planning how sure it can be about work of this kind and what it is held to: a venture
          bet is exploration and should carry little calendar confidence; a customer commitment has a fixed date. Most
          investment types need no workload of their own. Each type&apos;s pattern is versioned — a plan keeps the version
          it read.
        </>
      }
    />
  );
}
